import React, { useState, useEffect, useMemo } from 'react';
import { X, Filter } from 'lucide-react';
import { employeeDepartmentNames } from '../utils/employeeListFilters';

export const EmployeeFiltersModal = ({ isOpen, onClose, employees = [], filters, onApplyFilters }) => {
  const [localFilters, setLocalFilters] = useState({
    status: filters?.status || '',
    type: filters?.type || '',
    department: filters?.department || ''
  });

  // Obtener lista única de departamentos de los empleados
  const availableDepartments = useMemo(() => {
    const departments = new Set();
    employees.forEach((emp) => {
      employeeDepartmentNames(emp).forEach((name) => departments.add(name));
    });
    return Array.from(departments).sort((a, b) => a.localeCompare(b, 'es'));
  }, [employees]);

  useEffect(() => {
    if (isOpen) {
      setLocalFilters({
        status: filters?.status || '',
        type: filters?.type || '',
        department: filters?.department || ''
      });
    }
  }, [isOpen, filters]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApply = () => {
    onApplyFilters(localFilters);
    onClose();
  };

  const handleClearFilters = () => {
    const resetFilters = {
      status: '',
      type: '',
      department: ''
    };
    setLocalFilters(resetFilters);
    onApplyFilters(resetFilters);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-800 rounded-2xl border border-gray-700 shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Filter className="w-5 h-5 text-gray-400" />
            <h2 className="text-2xl font-semibold text-white">
              Filtros de Empleados
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Estado */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Estado
              </label>
              <select
                name="status"
                value={localFilters.status}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos</option>
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </select>
            </div>

            {/* Tipo */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tipo
              </label>
              <select
                name="type"
                value={localFilters.type}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos</option>
                <option value="internal">Interno</option>
                <option value="external">Externo</option>
              </select>
            </div>

            {/* Departamento */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Departamento
              </label>
              <select
                name="department"
                value={localFilters.department}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos</option>
                {availableDepartments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleClearFilters}
            className="px-4 py-2 rounded-lg border border-red-600 bg-red-600 text-white hover:bg-red-700 transition-colors"
          >
            Eliminar Filtros
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="px-4 py-2 rounded-lg border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            Aplicar Filtros
          </button>
        </div>
      </div>
    </div>
  );
};
